import { useState } from 'react';

import { ActionTypeE } from '../declarations';
import { AttachmentI } from 'declarations/interfaces/attachments';
import { NoopT } from 'declarations/types/common';
import { useDownloadAttachmentFromSignedUrl, useGetAttachmentUrl } from 'hooks/api/useAttachments';
import { useFetchTicketAttachments } from 'hooks/api/useTickets';

type UseViewAttachmentsPopupT = {
  attachments: AttachmentI[] | null;
  isLoadingAttachments: boolean;
  isAttachmentsFetchingError: boolean;
  isDeleteAttachmentPopupOpened: boolean;
  handleCloseDeleteAttachmentPopup: NoopT;
  selectedAttachment: AttachmentI | null;
  isLoadingAttachmentForDownload: boolean;
  isLoadingAttachmentForPreview: boolean;
  isAttachmentForDownloadError: boolean;
  isAttachmentForPreviewError: boolean;
  handleOpenDeleteAttachmentPopup: (attachment: AttachmentI) => NoopT;
  handleAttachmentClick: (attachment: AttachmentI, actionType: ActionTypeE) => NoopT;
};

type UseViewAttachmentsPopupParamsT = {
  ticketId: string;
  isPopupOpened: boolean;
};

const useViewAttachmentsPopup = ({
  ticketId,
  isPopupOpened,
}: UseViewAttachmentsPopupParamsT): UseViewAttachmentsPopupT => {
  /**
   * Hooks
   */
  const [selectedAttachment, selectAttachment] = useState<null | AttachmentI>(null);
  const [actionType, setActionType] = useState<null | ActionTypeE>(null);
  const [isDeleteAttachmentPopupOpened, setDeleteAttachmentPopupVisibility] = useState(false);

  /**
   * Variables
   */
  const shouldFetchAttachments = !!ticketId && isPopupOpened;
  const shouldViewAttachment = !!selectedAttachment && actionType === ActionTypeE.View;
  const shouldDownloadAttachment = !!selectedAttachment && actionType === ActionTypeE.Download;

  /**
   * Api hooks
   */
  const attachmentsResult = useFetchTicketAttachments({
    ticketId,
    shouldFetch: shouldFetchAttachments,
  });

  const attachmentForDownloadResult = useDownloadAttachmentFromSignedUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldDownloadAttachment,
    onSuccess: ({ data, contentType }) => {
      const fileName =
        (selectedAttachment?.originalName as string) || (selectedAttachment?.fileName as string);
      const blob = new Blob([data], { type: contentType });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      selectAttachment(null);
      setActionType(null);
    },
  });

  const attachmentForPreviewResult = useGetAttachmentUrl({
    ticketId,
    fileName: selectedAttachment?.fileName || '',
    shouldFetch: shouldViewAttachment,
    onSuccess: ({ value }) => {
      window.open(value, '_blank');

      selectAttachment(null);
      setActionType(null);
    },
  });

  /**
   * Handlers
   */
  const handleOpenDeleteAttachmentPopup = (attachment: AttachmentI) => () => {
    setDeleteAttachmentPopupVisibility(true);
    selectAttachment(attachment);
  };
  const handleCloseDeleteAttachmentPopup = () => {
    setDeleteAttachmentPopupVisibility(false);
    selectAttachment(null);
  };
  const handleAttachmentClick = (attachment: AttachmentI, actionType: ActionTypeE) => () => {
    selectAttachment(attachment);
    setActionType(actionType);
  };

  return {
    attachments: attachmentsResult.data ?? null,
    isLoadingAttachments: attachmentsResult.isLoading || attachmentsResult.isRefetching,
    isAttachmentsFetchingError: attachmentsResult.isError,
    isDeleteAttachmentPopupOpened,
    handleOpenDeleteAttachmentPopup,
    handleCloseDeleteAttachmentPopup,
    selectedAttachment,
    handleAttachmentClick,
    isLoadingAttachmentForDownload: attachmentForDownloadResult.isFetching,
    isLoadingAttachmentForPreview: attachmentForPreviewResult.isFetching,
    isAttachmentForDownloadError: attachmentForDownloadResult.isError,
    isAttachmentForPreviewError: attachmentForPreviewResult.isError,
  };
};

export default useViewAttachmentsPopup;
